import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useRegion, useRegionSearch } from "../cspServices";
import Autocomplete from "@mui/material/Autocomplete";
import { isBlank, notBlank } from "../../util/ext";
import { equals, find, propEq } from "ramda";
import TextField from "@mui/material/TextField";
import { useDebounce } from "../../util/hooks";
import VirtualizedListbox, {
  LISTBOX_BORDER_STYLE,
} from "../../components/VirtualizedListbox";

const RegionSelect = props => {
  const { onChange, value, error = false, helperText } = props;
  const [open, setOpen] = useState(false);

  const [searchString, setSearchString] = useState("");
  const debouncedSearchTerm = useDebounce(searchString, 500);

  const [selectedRegion, setSelectedRegion] = useState(null);
  const regions = useRegionSearch(debouncedSearchTerm);
  const [regionList, setRegionList] = useState([]);

  const region = useRegion(value);
  useEffect(() => {
    if (region.isSuccess) {
      setSearchString(region.data.name);
    }
  }, [region.isSuccess, region.data, setSearchString]);

  useEffect(() => {
    if (regions.isSuccess) {
      setRegionList(regions.data);
      setSelectedRegion(find(propEq("code", value), regions.data));
    } else {
      setRegionList([]);
      setSelectedRegion(null);
    }
  }, [
    regions.isSuccess,
    regions.data,
    value,
    setOpen,
    setRegionList,
    setSelectedRegion,
    searchString,
  ]);

  const handleRegionSelect = useCallback(
    (_event, region) => {
      setSelectedRegion(region);
      onChange(region ? region.code : null);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (event, value, reason) => {
      if (reason === "input") {
        notBlank(value) ? setSearchString(value) : setSearchString("");
        setOpen(true);
      } else if (reason === "clear") {
        setRegionList([]);
        setSearchString("");
        setOpen(true);
      }
    },
    [setSearchString],
  );

  return (
    <Fragment>
      {regions.isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          disableListWrap
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          selectOnFocus
          handleHomeEndKeys
          options={regionList}
          value={selectedRegion || null}
          inputValue={
            isBlank(selectedRegion) ? searchString : selectedRegion.name
          }
          ListboxComponent={VirtualizedListbox}
          ListboxProps={{
            itemSize: 50,
            listboxPadding: 4,
            listStyle: LISTBOX_BORDER_STYLE,
          }}
          getOptionLabel={option => notBlank(option) && `${option.name}`}
          isOptionEqualToValue={(opt, val) =>
            notBlank(val) && equals(opt.code, val.code)
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Box fontSize="1.4rem">{option.name}</Box>
                <Box fontSize="1.2rem">{`(${option.countryCode})`}</Box>
              </Box>
            </li>
          )}
          onChange={handleRegionSelect}
          onInputChange={handleInputChange}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                type="text"
                name={"region-search-" + Math.random()}
                variant="standard"
                label="Region"
                value={searchString}
                error={error}
                helperText={helperText}
                autoFocus={notBlank(searchString) || open}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

export default RegionSelect;
